import React from 'react';
import { am5, am5themes_Animated, am5xy, mui } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import { createAxisAndSeries } from './methods/create-axis-and-series';
import { createXAxis } from './methods/create-x-axis';
import { FONT_FAMILY, FONT_SIZE } from '../../../theme/layout-size';
import { createDownloadButton } from '../shared/create-download-button';
import { handleZoom } from '../shared/handle-zoom';
import { getPaddingLeft } from '../shared/helpers';

export type ChartProps = {
  id: string;
  data: types.widgets.TableData;
  fullScreen: boolean;
  numberFormatLeft: string;
  numberFormatRight: string;
  download: boolean;
  customDownloadFormatting?: (_data: types.widgets.TableData) => types.widgets.TableData;
  title: string | undefined;
  inSameScale: string[] | undefined;
};

const Chart = ({
  id,
  data,
  fullScreen,
  numberFormatLeft,
  numberFormatRight,
  download,
  customDownloadFormatting,
  title,
  inSameScale,
}: ChartProps) => {
  const theme = mui.styles.useTheme() as mui.core.Theme;
  const widgetId = `chart-${id}-${fullScreen ? 'fs' : 'sw'}`;
  const dataLength = Object.keys(data?.[0] ?? {}).length;
  const shouldShowLegend = dataLength > 1 && (fullScreen || dataLength <= 6);

  // Calculate if dataset is large for performance optimizations
  const seriesCount = dataLength - 1;
  const totalDataPoints = data.length * seriesCount;
  const isLargeDataset = totalDataPoints > 10_000;

  React.useEffect(() => {
    // Create root element
    const root = am5.Root.new(widgetId);

    // Remove amCharts logo
    root._logo?.dispose();

    // Create chart
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'panX',
        wheelY: 'zoomX',
        pinchZoomX: false,
        layout: root.verticalLayout,
        paddingLeft: getPaddingLeft(download),
        paddingBottom: shouldShowLegend ? 0 : 20,
        maxTooltipDistance: 0,
      })
    );

    // Disable animations for faster initial load with large datasets
    if (!isLargeDataset) {
      root.setThemes([am5themes_Animated.new(root)]);
      chart.appear(1000, 100);
    } else {
      chart.appear(0, 0);
    }

    if (title) {
      chart.children.unshift(
        am5.Label.new(root, {
          text: title,
          fontSize: 15,
          x: am5.p50,
          centerX: am5.p50,
          paddingTop: 0,
          paddingBottom: 0,
          marginBottom: 20,
        })
      );
    }

    const xAxis = createXAxis({
      root,
      chart,
      dataLength: data.length,
      isLargeDataset,
    });

    const createdSeries = createAxisAndSeries({
      root,
      chart,
      data,
      numberFormatLeft,
      numberFormatRight,
      xAxisRenderer: xAxis,
      inSameScale,
      isLargeDataset,
    });

    if (shouldShowLegend) {
      const scrollbar = am5.Scrollbar.new(root, {
        orientation: 'vertical',
      });

      const legend = chart.children.push(
        am5.Legend.new(root, {
          nameField: 'name',
          fillField: 'color',
          strokeField: 'color',
          centerX: am5.percent(50),
          x: am5.percent(50),
          useDefaultMarker: true,
          paddingTop: 20,
          paddingBottom: 20,
          maxHeight: 100,
          layout: root.gridLayout,
          verticalScrollbar: scrollbar,
        })
      );

      legend.labels.template.setAll({
        fontSize: FONT_SIZE.xxs,
        fontFamily: FONT_FAMILY,
      });

      legend.itemContainers.template.setAll({
        marginTop: 0,
        marginBottom: 0,
        paddingTop: 2,
        paddingBottom: 2,
        paddingLeft: 5,
        paddingRight: 5,
      });

      legend.markerRectangles.template.setAll({
        cornerRadiusTL: 0,
        cornerRadiusTR: 0,
        cornerRadiusBL: 0,
        cornerRadiusBR: 0,
      });
      legend.markers.template.setAll({
        width: chart.series.values.length > 2 ? 8 : 12,
        height: chart.series.values.length > 2 ? 8 : 12,
      });

      legend.data.setAll(chart.series.values);
    }

    chart.set(
      'cursor',
      am5xy.XYCursor.new(root, {
        behavior: 'zoomX',
        xAxis: xAxis,
        snapToSeries: createdSeries,
      })
    );

    const xAxisTooltip = xAxis.set(
      'tooltip',
      am5.Tooltip.new(root, {
        themeTags: ['axis'],
      })
    );
    xAxisTooltip.get('background').setAll({
      fill: am5.color(theme.palette.primary.dark),
    });
    xAxisTooltip.label.setAll({
      fontSize: 11,
    });

    handleZoom(chart);
    if (download) createDownloadButton(root, 'date', data, customDownloadFormatting);

    return () => {
      root.dispose();
    };
  }, [data, isLargeDataset]);

  return <div id={widgetId} style={{ width: '100%', height: '100%' }} />;
};

export default Chart;
